/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  deleteDocument,
  getDocumentFile,
  loadDocuments,
} from "../../../../services/documentsManager";
import DocumentHistoryModal from "../../../../components/DocumentsManager/DocumentHistoryModal";
import DocumentSettingsModal from "../../../../components/DocumentsManager/DocumentSettingsModal";
import DocumentsList from "../../../../components/DocumentsManager/DocumentsList";
import DocumentUploadModal from "../../../../components/DocumentsManager/DocumentUploadModal";
import {
  handleDownloadDocument,
  handleViewDocument,
} from "../../../../components/DocumentsManager/DocumentUtils";
import { useIsMobile } from "../../../../utils/useIsMobile";
import ModalDelete from "../../../../components/ModalDelete/ModalDelete";

interface prop {
  uid_cliente?: string;
}

const Documents = ({ uid_cliente }: prop) => {
  const entidade = "CUSTOMERS";
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [isHidden, setIsHidden] = useState<{
    uid?: string;
    isEdit?: boolean;
  }>({
    isEdit: false,
    uid: "",
  });

  const [preSelectedDocType, setPreSelectedDocType] = useState("");

  const [isModalUploadOpen, setIsModalUploadOpen] = useState(false);
  const [isModalConfigOpen, setIsModalConfigOpen] = useState(false);

  const [isModalHistoryOpen, setIsModalHistoryOpen] = useState(false);
  const [historyDocType, setHistoryDocType] = useState<string>("");
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  const { data: documentsData } = useQuery({
    queryKey: ["clientsDocuments", uid_cliente],
    queryFn: () => loadDocuments(uid_cliente!),
    enabled: !!uid_cliente,
  });

  const { mutateAsync: deleteDocMutation, isPending: deleteDocIsPending } = useMutation({
    mutationFn: deleteDocument,
    onSuccess: (response) => {
      toast.success(response.message || "Documento deletado com sucesso");
      queryClient.invalidateQueries({
        queryKey: ["clientsDocuments", uid_cliente],
      });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const handleClose = () => {
    setIsHidden((prev) => ({
      ...prev,
      isEdit: false,
      uid: "",
    }));
  };
  const handleView = useCallback(async (doc: any) => {
    await handleViewDocument(doc.uid_documento, getDocumentFile);
  }, []);

  const handleDownload = useCallback(async (doc: any) => {
    await handleDownloadDocument(doc.uid_documento, getDocumentFile);
  }, []);

  const handleDeleteDocument = useCallback((doc: any) => {
    setDeleteTarget(doc);
  }, []);

  const confirmDelete = async () => {
    if (deleteTarget) {
      try {
        await deleteDocMutation(deleteTarget.uid_documento);
        setDeleteTarget(null); // Fecha o modal após sucesso
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleUploadVersion = useCallback((doc: any) => {
    if (doc.uid_tipo_documento) {
      setPreSelectedDocType(doc.uid_tipo_documento);
      setIsModalUploadOpen(true);
    } else {
      setPreSelectedDocType("");
      setIsModalUploadOpen(true);
    }
  }, []);

  const handleHistory = useCallback((doc: any) => {
    if (doc.uid_tipo_documento) {
      setHistoryDocType(doc.uid_tipo_documento);
      setIsModalHistoryOpen(true);
    }
  }, []);

  return (
    <div>
      <DocumentsList
        data={documentsData || []}
        onAdd={() => setIsModalUploadOpen(true)}
        onSettings={() => setIsModalConfigOpen(true)}
        onView={handleView}
        onDownload={handleDownload}
        onUpload={handleUploadVersion}
        onDelete={handleDeleteDocument}
        onCopy={handleHistory}
      />
      {isHidden.isEdit && (
        <DocumentUploadModal
          entidade={entidade}
          onClose={() => handleClose()}
          uid_vinculo={(uid_cliente as string) || ""}
          refetchEntity={["clientsDocuments"]}
          initialDocType={preSelectedDocType}
        />
      )}

      {isModalConfigOpen && (
        <DocumentSettingsModal
          onClose={() => setIsModalConfigOpen(false)}
          entidade={entidade}
        />
      )}
      {isModalUploadOpen && (
        <DocumentUploadModal
          onClose={() => {
            setIsModalUploadOpen(false);
            setPreSelectedDocType("");
          }}
          onSuccess={() => {
            setIsModalUploadOpen(false);
            setPreSelectedDocType("");
            queryClient.invalidateQueries({
              queryKey: ["clientsDocuments"],
            });
          }}
          entidade={entidade}
          uid_vinculo={uid_cliente || ""}
          initialDocType={preSelectedDocType}
        />
      )}

      {isModalHistoryOpen && (
        <DocumentHistoryModal
          onClose={() => {
            setIsModalHistoryOpen(false);
            setHistoryDocType("");
            queryClient.invalidateQueries({
              queryKey: ["clientsDocuments"],
            });
          }}
          entidade={entidade}
          uid_vinculo={uid_cliente || ""}
          initialDocType={historyDocType}
        />
      )}

      <ModalDelete
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isDeleting={deleteDocIsPending} // Usa o estado do react-query
        entity="documento"
        title={deleteTarget?.titulo || ""}
        isMobile={isMobile}
      />
    </div>
  );
};

export default Documents;
